/**
 * @fileoverview Contact Attempt Modal Component
 * @module components/modals/ContactAttemptModal
 * @description Opened from CreateEscalationModal's "Contact" link to log a
 * new contact attempt (date/time, who was contacted, and the method used)
 * against an escalation, independent of whatever Contact Status is currently
 * selected. Saving posts to the contact-attempts sub-resource - isSaving/
 * error reflect that request's in-flight/failed state so the modal stays
 * open for a retry rather than closing on failure. Follow-up needed/date
 * lives on the escalation itself (one flat field pair, shared across all
 * contact attempts), not here.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";
import {
  CONTACT_METHODS,
  ESCALATION_CONTACT_TYPES,
} from "../../constants/escalation";

const fieldLabelSx = {
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "35px",
  color: "#403C6F",
};

const selectSx = (hasError) => ({
  height: "45px",
  background: "#fff",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: hasError ? "#f44336" : "#E0E0E0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: hasError ? "#f44336" : "#BDBDBD",
  },
});

const FieldLabel = ({ children }) => (
  <Typography sx={fieldLabelSx}>
    {children}
    <Box component="span" sx={{ color: "#f44336" }}>
      {" *"}
    </Box>
  </Typography>
);

FieldLabel.propTypes = {
  children: PropTypes.node,
};

const FieldError = ({ message }) => {
  if (!message) return null;
  return (
    <Typography sx={{ color: "#f44336", fontSize: "12px", mt: "4px" }}>
      {message}
    </Typography>
  );
};

FieldError.propTypes = {
  message: PropTypes.string,
};

// Today's date as a local "YYYY-MM-DD" string, matching the <input
// type="date"> value format - used as that input's min attribute.
const todayDateString = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

// date/time are separate inputs, so only the combination can tell whether
// the moment being logged is in the past - "today" alone isn't enough since
// a same-day time could still already have passed. Parsed without a "Z"
// suffix, this is local time, matching how contactAt is built for submission
// (see combineDateTime in CreateEscalationModal).
const isPastDateTime = (date, time) => {
  if (!date || !time) return false;
  const selected = new Date(`${date}T${time}`);
  return !Number.isNaN(selected.getTime()) && selected < new Date();
};

const getRequiredError = (submitted, value, message) =>
  submitted && !value ? message : "";

const ContactAttemptModal = ({
  open,
  onClose,
  onSave,
  initialData,
  defaultContactedParty = "",
  isSaving = false,
  error = "",
}) => {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [contactedParty, setContactedParty] = useState("");
  const [method, setMethod] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setDate(initialData?.date || "");
      setTime(initialData?.time || "");
      // Defaults to whatever's currently picked in the Escalation Contact
      // dropdown - the attempt is almost always against that same contact,
      // but this is still just a starting point the user can change.
      setContactedParty(initialData?.contactedParty || defaultContactedParty);
      setMethod(initialData?.method || "");
      setSubmitted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialData]);

  const dateError = getRequiredError(submitted, date, "Date is required");
  const timeError = getRequiredError(submitted, time, "Time is required");
  const pastDateTimeError =
    submitted && isPastDateTime(date, time)
      ? "Date/time cannot be in the past"
      : "";
  const contactedPartyError = getRequiredError(
    submitted,
    contactedParty,
    "Contacted Party is required",
  );
  const methodError = getRequiredError(
    submitted,
    method,
    "Contact method is required",
  );

  const isFormValid =
    Boolean(date) &&
    Boolean(time) &&
    Boolean(contactedParty) &&
    Boolean(method) &&
    !isPastDateTime(date, time);

  const handleSubmit = () => {
    setSubmitted(true);

    if (!isFormValid) return;

    onSave({
      date,
      time,
      contactedParty,
      method,
    });
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      keepMounted={false}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            padding: { xs: "16px", md: "24px" },
            backgroundColor: "#F7F8FB",
            boxShadow: "none",
            border: "none",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          m: 0,
          width: "100%",
          height: "40px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "20px",
            lineHeight: "30px",
            color: "#000000",
          }}
        >
          Contact Attempt
        </Typography>
        <IconButton onClick={onClose} aria-label="Close contact attempt dialog">
          <CloseIcon sx={{ color: "#7F7C9F" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: 0, mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box>
            <FieldLabel>Date</FieldLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                value={date ? dayjs(date) : null}
                onChange={(newValue) => {
                  if (newValue === null) {
                    setDate("");
                  } else if (newValue.isValid()) {
                    setDate(newValue.format("YYYY-MM-DD"));
                  }
                }}
                minDate={dayjs(todayDateString())}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    error: Boolean(dateError) || Boolean(pastDateTimeError),
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        "& fieldset": {
                          borderColor: dateError ? "#f44336" : "#E0E0E0",
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
            <FieldError message={dateError} />
          </Box>

          <Box sx={{ mt: 1 }}>
            <FieldLabel>Time</FieldLabel>
            <TextField
              fullWidth
              type="time"
              size="small"
              value={time}
              onChange={(event) => setTime(event.target.value)}
              error={Boolean(timeError) || Boolean(pastDateTimeError)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  "& fieldset": {
                    borderColor: timeError ? "#f44336" : "#E0E0E0",
                  },
                },
              }}
            />
            <FieldError message={timeError || pastDateTimeError} />
          </Box>

          <Box sx={{ mt: 1 }}>
            <FieldLabel>Contacted Party</FieldLabel>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={contactedParty}
              onChange={(event) => setContactedParty(event.target.value)}
              renderValue={(selected) => selected || "Select Contacted Party"}
              sx={selectSx(Boolean(contactedPartyError))}
            >
              {ESCALATION_CONTACT_TYPES.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            <FieldError message={contactedPartyError} />
          </Box>

          <Box sx={{ mt: 1 }}>
            <FieldLabel>Contact Method</FieldLabel>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={method}
              onChange={(event) => setMethod(event.target.value)}
              renderValue={(selected) => selected || "Select Contact Method"}
              sx={selectSx(Boolean(methodError))}
            >
              {CONTACT_METHODS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            <FieldError message={methodError} />
          </Box>

          {submitted && !isFormValid && (
            <Typography sx={{ color: "#f44336", fontSize: "13px" }}>
              Please complete all required fields before saving.
            </Typography>
          )}

          {error && (
            <Typography sx={{ color: "#f44336", fontSize: "13px" }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "10px",
          mt: 1,
          p: 0,
        }}
      >
        <Button
          onClick={onClose}
          disabled={isSaving}
          sx={{
            color: "#403C6F",
            backgroundColor: "#FFFFFF",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            px: 3,
            borderRadius: "52px",
            "&:hover": { backgroundColor: "rgba(64, 60, 111, 0.04)" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSaving}
          sx={{
            backgroundColor: "#403C6F",
            color: "#FFFFFF",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            px: 3,
            borderRadius: "52px",
            "&:hover": {
              backgroundColor: "#4a4576",
            },
            "&:disabled": {
              backgroundColor: "#D4D4D6",
              color: "#403C6F",
              cursor: "not-allowed",
              opacity: 1,
            },
          }}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ContactAttemptModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    date: PropTypes.string,
    time: PropTypes.string,
    contactedParty: PropTypes.string,
    method: PropTypes.string,
  }),
  defaultContactedParty: PropTypes.string,
  isSaving: PropTypes.bool,
  error: PropTypes.string,
};

export default ContactAttemptModal;
